/**
 * Accounts domain query/mutation hooks - built on the shared apiClient
 * (src/lib/apiClient.js) via useRequest, not useAppServices(). Replaces the
 * inline useQuery/useMutation calls previously duplicated across:
 *   - layouts/super-admin/Accounts/index.js (list + delete)
 *   - layouts/super-admin/Accounts/AccountDetail.js (byId)
 *   - layouts/super-admin/Accounts/components/EditModal/index.js (update)
 *   - layouts/super-admin/Accounts/components/Tabs/{AccountTab,ContactTab,
 *     AccountPlan,SaasTab}.js (update)
 *   - layouts/super-admin/Accounts/components/Tabs/agentconfiguration.js
 *     (byId + update-plugin)
 *   - layouts/super-admin/Accounts/components/Tabs/PromptLabels.js (byId)
 *
 * accountKeys.all is the shared "accounts" prefix every one of these
 * invalidates - matches the resource-name convention already established
 * across the rest of the app's TanStack Query migration.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const accountKeys = {
  all: ["accounts"],
  list: (agencyId, type, page, search) => ["accounts", type, page, search, agencyId],
  byId: (id) => ["accounts", id],
};

export const freeAccountTemplateKeys = {
  all: ["free-account-templates"],
};

// Accounts/index.js's paginated/searchable list. newestFirst maps to the
// backend's opt-in `newest_first` param (agencyiframe-only sort - the same
// /accounts route is shared with other apps that never send this param, so
// their unsorted default order is untouched).
export function useAccountsList({ agencyId, type, page, search, limit, newestFirst = true }) {
  const request = useRequest();
  return useQuery({
    queryKey: accountKeys.list(agencyId, type, page, search),
    queryFn: async () => {
      const typeParam =
        type === "cancel" || type === "cancel_request"
          ? `&status=${type}`
          : type === "premium" || type === "free"
            ? `&type=${type}`
            : "";
      const { response } = await request(GET, "/accounts", {
        query: `agency_id=${agencyId}&search=${search}&page=${page}&limit=${limit}&newest_first=${newestFirst}${typeParam}`,
        toaster: false,
      });
      if (!response) return { data: [], page: 1, totalpages: 1, totalRecords: 0 };
      return {
        data: response?.data?.filter((item) => item.business_name !== "Bevis") || [],
        page: response?.page || 1,
        totalpages: response?.totalPages || 1,
        totalRecords: response?.totalRecords || 0,
      };
    },
    enabled: !!agencyId,
  });
}

// Shared by AccountDetail.js, PromptLabels.js, and agentconfiguration.js -
// same id, same key, one cache entry.
export function useAccountById(id) {
  const request = useRequest();
  return useQuery({
    queryKey: accountKeys.byId(id),
    queryFn: async () => {
      const { response } = await request(GET, "/accounts/getbyid", { query: `id=${id}` });
      return response || null;
    },
    enabled: !!id,
  });
}

// CreateModal/index.js - creates the account, then its login user in the
// same flow (an account with nobody able to log into it isn't useful).
export function useCreateAccount() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const newAccount = await request(POST, "/accounts", { payload });
      if (!newAccount.response) {
        throw newAccount.error || new Error("Failed to create account");
      }
      const userPayload = {
        userName: payload.email?.split("@")[0] || "",
        email: payload.email,
        send_workflow: true,
        activeLocation: [newAccount.response.data?.id],
        agency_id: payload.agency_id,
        role: "admin",
      };
      await request(POST, "/users", { payload: userPayload });
      return newAccount;
    },
    // Fire-and-forget: a background cache refresh failing must never make
    // an otherwise-successful create look like it failed.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// Shared by EditModal and every Tabs/* save action - all PUT /accounts with
// a different payload shape, same invalidation. `toaster` is a hook-call-time
// param (not per-mutate-call) since each existing call site hardcodes its own
// value the same way - same convention as agency.js's useUpdateAgency.
export function useUpdateAccount(toaster = false) {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/accounts", { payload, toaster }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

// dataTableData.js's unpaged "all accounts for this agency" list (AccountDetail
// mode) - distinct from useAccountsList's paginated/searchable table above.
export function useAccountsByAgency(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["accounts", "agency-list", agencyId],
    queryFn: async () => {
      const { response } = await request(GET, "/accounts", {
        query: `agency_id=${agencyId}`,
        toaster: false,
      });
      return response ? response.data : [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useDeleteAccount() {
  const request = useRequest();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/accounts", { query, toaster: false }),
  });
}

// CreateModal/index.js's template-chooser dropdown - fetches every template
// for the agency as a flat array, gated by `enabled` (only while the chooser
// step is open).
export function useFreeAccountTemplatesPicker(agencyId, enabled = true) {
  const request = useRequest();
  return useQuery({
    queryKey: [...freeAccountTemplateKeys.all, "picker", agencyId],
    queryFn: async () => {
      const { response } = await request(GET, "/free_account_template", {
        query: `agency_id=${agencyId}&limit=100`,
        toaster: false,
      });
      return response?.data || [];
    },
    enabled: !!agencyId && enabled,
  });
}

// FreeAccountTemplate/index.js's paginated/searchable table.
export function useFreeAccountTemplates({ agencyId, page, search, limit }) {
  const request = useRequest();
  return useQuery({
    queryKey: [...freeAccountTemplateKeys.all, "list", agencyId, page, search],
    queryFn: async () => {
      const { response } = await request(GET, "/free_account_template", {
        query: `agency_id=${agencyId}&page=${page}&limit=${limit}&search=${search}`,
        toaster: false,
      });
      if (!response) return { data: [], totalpages: 1 };
      return { data: response.data || [], totalpages: response.totalPages || 1 };
    },
    enabled: !!agencyId,
  });
}

// CreateModelTemplate/index.js's create/edit form. PUT requires `_id` in the
// body but the form's payload never includes it, so it's injected here from
// the templateId the caller already has (same shape as the backend's Update
// handler expects). No toaster - the caller already shows its own
// window.alert on error and simply closes on success.
export function useSaveFreeAccountTemplate(isEditMode, templateId) {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      isEditMode
        ? request(PUT, "/free_account_template", { payload: { ...payload, _id: templateId } })
        : request(POST, "/free_account_template", { payload }),
    onSuccess: ({ response }) => {
      if (response) queryClient.invalidateQueries({ queryKey: freeAccountTemplateKeys.all });
    },
  });
}

export function useDeleteFreeAccountTemplate() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      request(DELETE, "/free_account_template", { query: `_id=${id}`, toaster: true }),
    onSuccess: ({ response }) => {
      if (response) queryClient.invalidateQueries({ queryKey: freeAccountTemplateKeys.all });
    },
  });
}

// agentconfiguration.js's plugin toggle/save.
export function useUpdateAccountPlugin() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/update-plugin", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.all }),
  });
}
